/**
 * Supabase Storage Adapter
 * P1.2: Uses API client to persist memories to Supabase
 */

import type { Memory } from "@/lib/constants/memory"
import type { IStorageAdapter, SaveMemoryInput, MemoryUpdates } from "../storage-adapter"
import { memoryApi, type ApiError } from "../api-client"

// ============================================================================
// Local Cache
// ============================================================================

interface CacheEntry {
  memories: Memory[]
  timestamp: number
}

const CACHE_TTL = 30000 // 30 seconds
let cache: CacheEntry | null = null

function isCacheValid(): boolean {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_TTL
}

function invalidateCache(): void {
  cache = null
}

// ============================================================================
// Supabase Adapter
// ============================================================================

class SupabaseAdapter implements IStorageAdapter {
  readonly name = "supabase"
  readonly isReadOnly = false

  /**
   * Get all memories, optionally including archived
   */
  async getMemories(includeArchived = false): Promise<Memory[]> {
    // Use cache if valid
    if (isCacheValid() && cache) {
      const memories = cache.memories
      return includeArchived ? memories : memories.filter((m) => !m.isArchived)
    }

    try {
      const memories = await memoryApi.fetchMemories()

      // Update cache
      cache = {
        memories,
        timestamp: Date.now(),
      }

      return includeArchived ? memories : memories.filter((m) => !m.isArchived)
    } catch (error) {
      console.error("[SupabaseAdapter] Failed to fetch memories:", error)
      // Return cached data if available, even if stale
      if (cache) {
        const memories = cache.memories
        return includeArchived ? memories : memories.filter((m) => !m.isArchived)
      }
      throw error
    }
  }

  /**
   * Get a single memory by field ID
   */
  async getMemoryByField(fieldId: string): Promise<Memory | undefined> {
    const memories = await this.getMemories(true)
    return memories.find((m) => m.field === fieldId)
  }

  /**
   * Save a new memory or update existing by field
   */
  async saveMemory(input: SaveMemoryInput): Promise<void> {
    try {
      // API handles upsert logic (creates if not exists, updates if exists)
      await memoryApi.createMemory({
        field: input.field,
        label: input.label,
        value: input.value,
        section: input.section,
        sectionLabel: input.sectionLabel,
        category: input.category,
        categoryLabel: input.categoryLabel,
      })

      invalidateCache()
    } catch (error) {
      console.error("[SupabaseAdapter] Failed to save memory:", error)
      throw error
    }
  }

  /**
   * Update an existing memory by ID
   */
  async updateMemory(id: string, updates: MemoryUpdates): Promise<void> {
    try {
      await memoryApi.updateMemory(id, {
        value: updates.value,
        label: updates.label,
        section: updates.section,
        sectionLabel: updates.sectionLabel,
        category: updates.category,
        categoryLabel: updates.categoryLabel,
        isArchived: updates.isArchived,
      })

      invalidateCache()
    } catch (error) {
      console.error("[SupabaseAdapter] Failed to update memory:", error)
      throw error
    }
  }

  /**
   * Delete a memory by ID
   */
  async deleteMemory(id: string): Promise<void> {
    try {
      await memoryApi.deleteMemory(id)
      invalidateCache()
    } catch (error) {
      console.error("[SupabaseAdapter] Failed to delete memory:", error)
      throw error
    }
  }

  /**
   * Archive a memory (soft delete)
   */
  async archiveMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: true })
  }

  /**
   * Restore an archived memory
   */
  async restoreMemory(id: string): Promise<void> {
    await this.updateMemory(id, { isArchived: false })
  }

  /**
   * Clear all memories (requires confirmation)
   */
  async clearAll(): Promise<void> {
    try {
      const memories = await this.getMemories(true)

      // Delete each memory
      await Promise.all(memories.map((m) => memoryApi.deleteMemory(m.id!)))

      invalidateCache()
    } catch (error) {
      console.error("[SupabaseAdapter] Failed to clear memories:", error)
      throw error
    }
  }

  /**
   * Get key details completed count
   * Note: This is stored in sessionStorage even for Supabase mode
   * to allow unauthenticated onboarding flow
   */
  async getKeyDetailsCompleted(): Promise<number> {
    if (typeof window === "undefined") return 0

    // For Supabase adapter, count actual core memories
    try {
      const memories = await this.getMemories(false)
      const coreFields = ["business_name", "industry", "products_services", "target_market", "current_goals"]
      const completedCount = memories.filter((m) => coreFields.includes(m.field) && m.value.trim() !== "").length
      return Math.min(5, completedCount)
    } catch {
      // Fallback to sessionStorage
      try {
        const stored = sessionStorage.getItem("biziq_key_details_completed")
        return stored ? Math.min(5, Number.parseInt(stored, 10)) : 0
      } catch {
        return 0
      }
    }
  }
}

// ============================================================================
// Export
// ============================================================================

let instance: SupabaseAdapter | null = null

export function getSupabaseAdapter(): IStorageAdapter {
  if (!instance) {
    instance = new SupabaseAdapter()
  }
  return instance
}

/**
 * Check if API is available (for health checks)
 */
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    await memoryApi.fetchMemories()
    return true
  } catch (error) {
    const apiError = error as ApiError
    // 401 means API is working, just not authenticated
    if (apiError.status === 401) return true
    return false
  }
}

/**
 * Force cache refresh
 */
export function refreshSupabaseCache(): void {
  invalidateCache()
}
